import React, { useState, useEffect } from 'react';
import { WritingPrompt, WritingSubmission } from '../types/models';
import '../styles/WritingInterface.css';

interface WritingInterfaceProps {
  prompt: WritingPrompt | null;
  studentProfileId: string;
  onSubmissionSuccess?: (submission: WritingSubmission) => void;
  onSubmissionError?: (error: string) => void;
  isLoading?: boolean;
}

/**
 * Component for writing submissions with a clean, distraction-free interface
 * Provides text editor, word count display, and submission functionality
 */
export const WritingInterface: React.FC<WritingInterfaceProps> = ({
  prompt,
  studentProfileId,
  onSubmissionSuccess,
  onSubmissionError,
  isLoading = false,
}) => {
  const [content, setContent] = useState<string>('');
  const [wordCount, setWordCount] = useState<number>(0);
  const [isSubmitting, setIsSubmitting] = useState<boolean>(false);
  const [submissionStatus, setSubmissionStatus] = useState<string>('');
  const [error, setError] = useState<string>('');
  const [savedDraft, setSavedDraft] = useState<boolean>(false);

  // Update word count when content changes
  useEffect(() => {
    const words = content.trim() ? content.trim().split(/\s+/).length : 0;
    setWordCount(words);
    
    // Auto-save draft
    if (content.trim()) {
      saveDraft();
    }
  }, [content]);

  // Load draft from localStorage on mount
  useEffect(() => {
    if (prompt) {
      const draftKey = `draft-${studentProfileId}-${prompt.id}`;
      const savedContent = localStorage.getItem(draftKey);
      if (savedContent) {
        setContent(savedContent);
        setSavedDraft(true);
      }
    }
  }, [prompt, studentProfileId]);

  const saveDraft = () => {
    if (prompt && content.trim()) {
      const draftKey = `draft-${studentProfileId}-${prompt.id}`;
      localStorage.setItem(draftKey, content);
      setSavedDraft(true);
    }
  };

  const clearDraft = () => {
    if (prompt) {
      const draftKey = `draft-${studentProfileId}-${prompt.id}`;
      localStorage.removeItem(draftKey);
      setSavedDraft(false);
    }
  };

  const handleSubmit = async () => {
    setError('');
    setSubmissionStatus('');

    // Validate content
    if (!content.trim()) {
      setError('Please write something before submitting');
      if (onSubmissionError) {
        onSubmissionError('Submission cannot be empty');
      }
      return;
    }

    if (!prompt) {
      setError('No prompt selected');
      if (onSubmissionError) {
        onSubmissionError('No prompt available');
      }
      return;
    }

    setIsSubmitting(true);
    setSubmissionStatus('Submitting your writing...');

    try {
      const apiUrl = import.meta.env.VITE_API_URL || 'http://localhost:3001';
      
      // Get token from localStorage
      const storedTokens = localStorage.getItem('authTokens');
      const token = storedTokens ? JSON.parse(storedTokens).idToken : null;
      
      const response = await fetch(`${apiUrl}/api/submissions/create`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...(token && { 'Authorization': `Bearer ${token}` }),
        },
        body: JSON.stringify({
          studentProfileId,
          promptId: prompt.id,
          content,
        }),
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.error || 'Failed to submit writing');
      }

      setSubmissionStatus('Submission successful! Starting assessment...');
      
      // Clear draft after successful submission
      clearDraft();
      
      // Reset form
      setContent('');
      
      const submissionId = data.submissionId;
      
      // Trigger the assessment
      setSubmissionStatus('Analyzing your writing...');
      const assessmentResponse = await fetch(`${apiUrl}/api/assessments`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...(token && { 'Authorization': `Bearer ${token}` }),
        },
        body: JSON.stringify({
          submissionId,
          yearLevel: prompt.yearLevel,
          testFormat: prompt.testFormat,
          promptText: prompt.promptText,
        }),
      });
      
      const assessmentData = await assessmentResponse.json();
      
      if (!assessmentResponse.ok) {
        // If assessment fails, still allow viewing the submission
        console.error('Assessment failed:', assessmentData.error);
        setSubmissionStatus('Assessment is taking longer than expected...');
      }
      
      let assessmentId: string | undefined = assessmentData.assessmentId;
      
      // If assessment completed immediately, we're done
      if (assessmentData.success && assessmentId) {
        setSubmissionStatus('Assessment complete! Loading your report...');
      } else {
        // Poll for assessment status (max 60 seconds)
        for (let i = 0; i < 30; i++) {
          await new Promise(resolve => setTimeout(resolve, 2000));
          setSubmissionStatus(`Analyzing your writing... (${i * 2 + 2}s)`);
          
          try {
            const statusResponse = await fetch(`${apiUrl}/api/assessments/status/${submissionId}`, {
              headers: { 
                'Content-Type': 'application/json',
                ...(token && { 'Authorization': `Bearer ${token}` }),
              },
            });
            const statusData = await statusResponse.json();
            
            if (statusData.isComplete && statusData.assessmentId) {
              assessmentId = statusData.assessmentId;
              setSubmissionStatus('Assessment complete! Loading your report...');
              break;
            }
            
            if (statusData.status === 'failed') {
              throw new Error('Assessment failed. Please try again.');
            }
          } catch (pollError) {
            // Continue polling on error
            console.log('Polling error:', pollError);
          }
        }
      }
      
      // Call success callback with assessmentId
      if (onSubmissionSuccess) {
        onSubmissionSuccess({
          id: submissionId,
          studentProfileId,
          promptId: prompt.id,
          content,
          wordCount: data.wordCount,
          submittedAt: new Date(),
          status: assessmentId ? 'completed' : 'processing',
          assessmentId,
        });
      }

      // Clear success message after 3 seconds
      setTimeout(() => {
        setSubmissionStatus('');
      }, 3000);
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'An error occurred';
      setError(errorMessage);
      setSubmissionStatus('');
      
      if (onSubmissionError) {
        onSubmissionError(errorMessage);
      }

      // Preserve draft on failure
      saveDraft();
    } finally {
      setIsSubmitting(false);
    }
  };

  if (!prompt) {
    return (
      <div className="writing-interface writing-interface--empty">
        <p>Please select a prompt to begin writing.</p>
      </div>
    );
  }

  return (
    <div className="writing-interface">
      {/* Header with prompt info */}
      <div className="writing-interface__header">
        <div className="writing-interface__prompt-info">
          <h2 className="writing-interface__title">Writing Submission</h2>
          <p className="writing-interface__prompt-category">
            {prompt.category.charAt(0).toUpperCase() + prompt.category.slice(1)} Writing
          </p>
        </div>
        <div className="writing-interface__meta">
          <span className="writing-interface__badge">
            {prompt.testFormat.toUpperCase()}
          </span>
          <span className="writing-interface__badge">
            Year {prompt.yearLevel}
          </span>
        </div>
      </div>

      {/* Main writing area */}
      <div className="writing-interface__content">
        {/* Text editor */}
        <div className="writing-interface__editor-container">
          <textarea
            className="writing-interface__textarea"
            placeholder="Start writing here... Your work is automatically saved as a draft."
            value={content}
            onChange={(e) => setContent(e.target.value)}
            disabled={isSubmitting || isLoading}
            aria-label="Writing submission text area"
          />
        </div>

        {/* Word count and status bar */}
        <div className="writing-interface__footer">
          <div className="writing-interface__word-count">
            <span className="writing-interface__word-count-label">Words:</span>
            <span className="writing-interface__word-count-value">{wordCount}</span>
          </div>

          <div className="writing-interface__status">
            {savedDraft && !isSubmitting && (
              <span className="writing-interface__draft-indicator">
                ✓ Draft saved
              </span>
            )}
            {submissionStatus && (
              <span className="writing-interface__submission-status">
                {submissionStatus}
              </span>
            )}
            {error && (
              <span className="writing-interface__error-message">
                {error}
              </span>
            )}
          </div>
        </div>
      </div>

      {/* Action buttons */}
      <div className="writing-interface__actions">
        <button
          className="writing-interface__button writing-interface__button--secondary"
          onClick={() => setContent('')}
          disabled={isSubmitting || isLoading || !content}
          aria-label="Clear writing"
        >
          Clear
        </button>
        <button
          className="writing-interface__button writing-interface__button--primary"
          onClick={handleSubmit}
          disabled={isSubmitting || isLoading || !content.trim()}
          aria-label="Submit writing"
        >
          {isSubmitting ? 'Submitting...' : 'Submit Writing'}
        </button>
      </div>
    </div>
  );
};

export default WritingInterface;
